import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuestionnaireTextUndoContext } from './QuestionnaireTextUndoContext';
import {
  applyExternalTextReplace,
  applyUserTextEdit,
  createTextUndoStackState,
  popTextRedo,
  popTextUndo,
  seedTextUndoBaseline,
  type TextUndoStackState,
} from './questionnaireTextUndoStack';

const DEFAULT_MAX_STACK = 100;

export interface UseQuestionnaireTextUndoOptions {
  fieldKey: string;
  value: string;
  onChange: (next: string) => void;
  maxStack?: number;
}

export interface UseQuestionnaireTextUndoResult {
  /** Value bound to the input/editor. */
  displayValue: string;
  /** Commit a user-driven edit (pushes prior value onto the undo stack). */
  commitUserEdit: (next: string) => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Per-field undo/redo stack for questionnaire text answers.
 * External replacements (e.g. AI) push the previous value unless skipped via baseline seeding.
 */
export function useQuestionnaireTextUndo({
  fieldKey,
  value,
  onChange,
  maxStack = DEFAULT_MAX_STACK,
}: UseQuestionnaireTextUndoOptions): UseQuestionnaireTextUndoResult {
  const undoCtx = useQuestionnaireTextUndoContext();
  const stackStateRef = useRef<TextUndoStackState>(createTextUndoStackState(value));
  const skipExternalPushRef = useRef(false);
  const baselineConsumedRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(value);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  const syncHistoryState = useCallback(() => {
    setCanUndo(stackStateRef.current.stack.length > 0);
    setCanRedo(stackStateRef.current.redoStack.length > 0);
  }, []);

  useEffect(() => {
    // Reset only when the field changes; read the value at that moment (a dep on `value` would reset on every keystroke).
    const initialValue = valueRef.current;
    stackStateRef.current = createTextUndoStackState(initialValue);
    baselineConsumedRef.current = false;
    setDisplayValue(initialValue);
    setCanUndo(false);
    setCanRedo(false);
  }, [fieldKey]);

  // AI pre-generation snapshot: seed undo stack once per field key.
  useEffect(() => {
    if (baselineConsumedRef.current) return;
    const baseline = undoCtx?.consumeAiBaseline(fieldKey);
    if (baseline === undefined) return;
    baselineConsumedRef.current = true;
    stackStateRef.current = seedTextUndoBaseline(stackStateRef.current, baseline);
    syncHistoryState();
  }, [fieldKey, undoCtx, syncHistoryState]);

  // Parent-driven value changes (AI replace, form hydrate) — push prior committed value.
  useEffect(() => {
    if (value === stackStateRef.current.committed) return;
    if (skipExternalPushRef.current) {
      skipExternalPushRef.current = false;
      stackStateRef.current = { ...stackStateRef.current, committed: value };
      setDisplayValue(value);
      return;
    }
    stackStateRef.current = applyExternalTextReplace(stackStateRef.current, value, maxStack);
    setDisplayValue(value);
    syncHistoryState();
  }, [value, maxStack, syncHistoryState]);

  const commitUserEdit = useCallback(
    (next: string) => {
      const updated = applyUserTextEdit(stackStateRef.current, next, maxStack);
      if (!updated) return;
      stackStateRef.current = updated;
      setDisplayValue(next);
      syncHistoryState();
      onChange(next);
    },
    [maxStack, onChange, syncHistoryState],
  );

  const undo = useCallback(() => {
    const popped = popTextUndo(stackStateRef.current);
    if (!popped) {
      syncHistoryState();
      return false;
    }
    skipExternalPushRef.current = true;
    stackStateRef.current = popped.state;
    setDisplayValue(popped.restored);
    syncHistoryState();
    onChange(popped.restored);
    return true;
  }, [onChange, syncHistoryState]);

  const redo = useCallback(() => {
    const popped = popTextRedo(stackStateRef.current);
    if (!popped) {
      syncHistoryState();
      return false;
    }
    skipExternalPushRef.current = true;
    stackStateRef.current = popped.state;
    setDisplayValue(popped.restored);
    syncHistoryState();
    onChange(popped.restored);
    return true;
  }, [onChange, syncHistoryState]);

  return {
    displayValue,
    commitUserEdit,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}
